/**
 * WorkspaceAccessContext
 *
 * Single source of truth for workspace billing/access state.
 * Fetches get_billing_status() once per session and makes the result
 * available everywhere in the protected-route tree.
 *
 * Usage:
 *   const { readOnly, canLaunchOnboarding } = useWorkspaceAccess()
 *
 * Rules:
 *  - Loading / no tenant  → all permissions are treated as ALLOWED (fail open)
 *    so the UI never breaks while auth is still hydrating.
 *  - PLANS_DISABLED=true  → full access, no RPC call.
 *  - Only admin/workspace routes need this; portal routes are unaffected.
 *
 * Stripe integration hook:
 *  Call refresh() after a successful Stripe checkout redirect to re-fetch
 *  the updated workspace status without a full page reload.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { supabase } from '../lib/supabase'
import { PLANS_DISABLED } from '../flags'
import { useTenant } from './TenantContext'

// ─── Types ────────────────────────────────────────────────────────────────────

export type WorkspaceStatus =
  | 'loading'
  | 'trial'
  | 'active'
  | 'expired_trial'
  | 'past_due'
  | 'cancelled'
  | 'error'

export interface WorkspaceAccessState {
  // ── Workspace status ─────────────────────────────────────────────────────
  workspaceStatus:      WorkspaceStatus
  isLoading:            boolean

  // ── Access flags (all true while loading to avoid flash of disabled UI) ──
  readOnly:             boolean
  canAddEmployees:      boolean
  canLaunchOnboarding:  boolean
  canUseAI:             boolean
  canEditTemplates:     boolean

  // ── Trial / seat info ────────────────────────────────────────────────────
  trialActive:          boolean
  trialDaysLeft:        number
  inGracePeriod:        boolean
  graceDaysLeft:        number
  seatsUsed:            number
  /** Infinity = unlimited (plan seats_included IS NULL) */
  seatsIncluded:        number
  seatsAvailable:       number
  planName:             string
  planDisplayName:      string
  /** Plan entitlement flags from billing_plans.features (e.g. sso, ai_assist) */
  features:             Record<string, boolean>

  // ── Error info ────────────────────────────────────────────────────────────
  /**
   * Set when get_billing_status fails. Non-null means the billing state is
   * unknown and all write actions are blocked (fail-closed).
   */
  billingError:         string | null

  // ── Utils ─────────────────────────────────────────────────────────────────
  /** Re-fetch billing status (call after Stripe checkout completes). */
  refresh:              () => void
}

// ─── Default state (full access while loading) ───────────────────────────────

const LOADING_STATE: WorkspaceAccessState = {
  workspaceStatus:     'loading',
  isLoading:           true,
  readOnly:            false,
  canAddEmployees:     true,
  canLaunchOnboarding: true,
  canUseAI:            true,
  canEditTemplates:    true,
  trialActive:         false,
  trialDaysLeft:       0,
  inGracePeriod:       false,
  graceDaysLeft:       0,
  seatsUsed:           0,
  seatsIncluded:       Infinity,
  seatsAvailable:      Infinity,
  planName:            'trial',
  planDisplayName:     'Trial',
  features:            {},
  billingError:        null,
  refresh:             () => {},
}

/**
 * Fail-CLOSED state: used when get_billing_status RPC errors with a known tenantId.
 * All write actions are blocked until the user retries and billing resolves.
 * This prevents expired/unpaid workspaces from retaining write access on transient errors.
 */
const BILLING_ERROR_STATE: WorkspaceAccessState = {
  workspaceStatus:     'error',
  isLoading:           false,
  readOnly:            true,
  canAddEmployees:     false,
  canLaunchOnboarding: false,
  canUseAI:            false,
  canEditTemplates:    false,
  trialActive:         false,
  trialDaysLeft:       0,
  inGracePeriod:       false,
  graceDaysLeft:       0,
  seatsUsed:           0,
  seatsIncluded:       0,
  seatsAvailable:      0,
  planName:            'unknown',
  planDisplayName:     'Unknown',
  features:            {},
  billingError:        'Unable to verify your workspace plan. Please retry.',
  refresh:             () => {},
}

// When PLANS_DISABLED, always return full enterprise-like access.
const PLANS_DISABLED_STATE: WorkspaceAccessState = {
  ...LOADING_STATE,
  workspaceStatus:     'active',
  isLoading:           false,
  planName:            'enterprise (plans disabled)',
}

// ─── Context ──────────────────────────────────────────────────────────────────

const WorkspaceAccessContext = createContext<WorkspaceAccessState>(LOADING_STATE)

// ─── Provider ─────────────────────────────────────────────────────────────────

export function WorkspaceAccessProvider({ children }: { children: ReactNode }) {
  const { tenantId } = useTenant()
  const [state, setState] = useState<WorkspaceAccessState>(LOADING_STATE)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick(t => t + 1), [])

  useEffect(() => {
    // Short-circuit when plans are disabled (dev/test flag)
    if (PLANS_DISABLED) {
      setState({ ...PLANS_DISABLED_STATE, refresh })
      return
    }

    if (!tenantId) {
      // No tenant yet — keep loading state (fail open)
      setState({ ...LOADING_STATE, refresh })
      return
    }

    let cancelled = false

    const fetchStatus = async () => {
      setState(prev => ({ ...prev, isLoading: true, refresh }))

      try {
        const { data, error } = await supabase
          .rpc('get_billing_status', { p_tenant_id: tenantId })
          .single()

        if (cancelled) return

        if (error || !data) {
          if (import.meta.env.DEV) console.warn('[WorkspaceAccess] RPC error:', error)
          // On RPC error with a known tenant: fail CLOSED so expired/unpaid workspaces
          // cannot perform write actions while billing status is unverified.
          setState({ ...BILLING_ERROR_STATE, refresh })
          return
        }

        // Map RPC response → typed workspace state
        const ws = (data.workspace_status ?? deriveWorkspaceStatus(data)) as WorkspaceStatus

        setState({
          workspaceStatus:     ws,
          isLoading:           false,
          readOnly:            data.read_only          ?? (ws === 'expired_trial' || ws === 'cancelled'),
          canAddEmployees:     data.can_add_employees  ?? true,
          canLaunchOnboarding: data.can_launch_onboarding ?? data.can_add_employees ?? true,
          canUseAI:            data.can_use_ai         ?? !(data.read_only),
          canEditTemplates:    data.can_edit_templates ?? !(data.read_only),
          trialActive:         data.trial_active       ?? false,
          trialDaysLeft:       data.trial_days_left    ?? 0,
          inGracePeriod:       data.in_grace_period    ?? false,
          graceDaysLeft:       data.grace_days_left    ?? 0,
          seatsUsed:           data.seats_used         ?? 0,
          // NULL seat allowance = unlimited plan
          seatsIncluded:       data.seats_included     ?? Infinity,
          seatsAvailable:      data.seats_available    ?? Infinity,
          planName:            data.plan               ?? 'trial',
          planDisplayName:     data.plan_display_name  ?? 'Trial',
          features:            (data.features as Record<string, boolean>) ?? {},
          billingError:        null,
          refresh,
        })
      } catch (err) {
        if (!cancelled) {
          if (import.meta.env.DEV) console.error('[WorkspaceAccess] fetch threw:', err)
          setState({ ...BILLING_ERROR_STATE, refresh })
        }
      }
    }

    fetchStatus()
    return () => { cancelled = true }
  }, [tenantId, tick]) // re-fetch on refresh() or tenant change

  return (
    <WorkspaceAccessContext.Provider value={state}>
      {children}
    </WorkspaceAccessContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWorkspaceAccess(): WorkspaceAccessState {
  return useContext(WorkspaceAccessContext)
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Derive workspace_status from legacy get_billing_status response that doesn't
 * yet have the workspace_status column (graceful degradation during migration).
 */
function deriveWorkspaceStatus(data: {
  plan: string
  status: string
  trial_active: boolean
  in_grace_period: boolean
}): WorkspaceStatus {
  const plan   = data.plan   ?? 'trial'
  const status = data.status ?? 'active'

  if (plan === 'trial') {
    return data.trial_active ? 'trial' : 'expired_trial'
  }
  if (status === 'active')   return 'active'
  if (status === 'trialing') return 'trial'
  if (status === 'past_due') return 'past_due'
  if (status === 'canceled') return 'cancelled'
  return 'expired_trial'
}
